import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, FolderOpen, Link2, Loader2, Search, Trash2 } from "lucide-react";

import { searchFileLibrary } from "@/api/file_library";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export const WORKFLOW_DOCUMENT_LINK_MIN_SEARCH_LENGTH = 2;
export const WORKFLOW_DOCUMENT_LINK_SEARCH_LIMIT = 50;
export const WORKFLOW_DOCUMENT_LINK_HELPER_TEXT =
  "Links reference existing File Center files without moving them.";

export type WorkflowDocumentLinkRecord = {
  id: number;
  document_id: number;
  name: string;
  file_path: string | null;
  label: string | null;
  role: string;
  linked_by_name: string;
};

export type WorkflowDocumentRoleOption = {
  value: string;
  label: string;
};

export type WorkflowDocumentSearchResult = {
  id: number;
  name: string;
  alreadyLinked: boolean;
  inHighlightedFolder: boolean;
};

type UseWorkflowDocumentLinksOptions<TLink extends WorkflowDocumentLinkRecord> = {
  municipalityId: number;
  linksQueryKey: readonly unknown[];
  listLinks: () => Promise<TLink[]>;
  linkDocument: (documentId: number, role: string) => Promise<unknown>;
  unlinkDocument: (linkId: number) => Promise<void>;
  defaultRole: string;
  enabled?: boolean;
  onInvalidate?: () => void;
  /** File Center folder whose documents get badged and sorted first in search results. */
  highlightFolderId?: number | null;
};

// Generic over the link record so a caller with a richer payload (the grant
// links carry processing_status, ENG-1982) keeps its type through `links`
// and the list's renderLinkActions.
export function useWorkflowDocumentLinks<TLink extends WorkflowDocumentLinkRecord = WorkflowDocumentLinkRecord>({
  municipalityId,
  linksQueryKey,
  listLinks,
  linkDocument,
  unlinkDocument,
  defaultRole,
  enabled = true,
  onInvalidate,
  highlightFolderId = null,
}: UseWorkflowDocumentLinksOptions<TLink>) {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpenState] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const [documentSearch, setDocumentSearch] = useState("");
  const [debouncedDocumentSearch, setDebouncedDocumentSearch] = useState("");

  const { data: links = [], isLoading, error } = useQuery({
    queryKey: linksQueryKey,
    queryFn: listLinks,
    enabled,
  });

  const linkedDocumentIds = useMemo(() => links.map((link) => link.document_id), [links]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedDocumentSearch(documentSearch.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [documentSearch]);

  const searchReady =
    debouncedDocumentSearch.length >= WORKFLOW_DOCUMENT_LINK_MIN_SEARCH_LENGTH;

  const documentSearchQueryKey = useMemo(
    () => ["file_library_nl_search", municipalityId, debouncedDocumentSearch] as const,
    [municipalityId, debouncedDocumentSearch],
  );

  // Natural-language File Center search (name/description keywords + semantic
  // match). Already-linked documents stay in the results — the dialog renders
  // them disabled so users see why a file "isn't there" instead of an
  // unexplained gap.
  const { data: searchDocuments = [], isLoading: searchLoading } = useQuery({
    queryKey: documentSearchQueryKey,
    queryFn: ({ signal }) =>
      searchFileLibrary(municipalityId, {
        q: debouncedDocumentSearch,
        documentLimit: WORKFLOW_DOCUMENT_LINK_SEARCH_LIMIT,
        signal,
      }).then((payload) => payload.documents),
    enabled: enabled && pickerOpen && searchReady,
  });

  const searchResults: WorkflowDocumentSearchResult[] = useMemo(() => {
    const annotated = searchDocuments.map((document) => ({
      id: document.id,
      name: document.name,
      alreadyLinked: linkedDocumentIds.includes(document.id),
      inHighlightedFolder: highlightFolderId != null && document.group_id === highlightFolderId,
    }));
    // Stable partition: keep relevance order, but surface this record's own
    // folder first.
    return [
      ...annotated.filter((result) => result.inHighlightedFolder),
      ...annotated.filter((result) => !result.inHighlightedFolder),
    ];
  }, [searchDocuments, linkedDocumentIds, highlightFolderId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: linksQueryKey });
    onInvalidate?.();
  };

  const resetPicker = () => {
    setSelectedDocumentId("");
    setSelectedRole(defaultRole);
    setDocumentSearch("");
    setDebouncedDocumentSearch("");
  };

  const setPickerOpen = (open: boolean) => {
    if (!open) resetPicker();
    setPickerOpenState(open);
  };

  const linkMutation = useMutation({
    mutationFn: ({ documentId, role }: { documentId: number; role: string }) =>
      linkDocument(documentId, role),
    onSuccess: () => {
      invalidate();
      setPickerOpen(false);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: unlinkDocument,
    onSuccess: invalidate,
  });

  const openPicker = () => {
    resetPicker();
    setPickerOpenState(true);
  };

  return {
    links,
    isLoading,
    error,
    pickerOpen,
    setPickerOpen,
    openPicker,
    selectedDocumentId,
    setSelectedDocumentId,
    selectedRole,
    setSelectedRole,
    documentSearch,
    setDocumentSearch,
    searchResults,
    searchLoading,
    searchReady,
    linkMutation,
    unlinkMutation,
  };
}

type WorkflowDocumentLinkButtonProps = {
  onClick: () => void;
  className?: string;
};

export function WorkflowDocumentLinkButton({ onClick, className }: WorkflowDocumentLinkButtonProps) {
  return (
    <Button size="sm" variant="outline" className={className} onClick={onClick}>
      <Link2 className="mr-2 h-4 w-4" />
      Link file
    </Button>
  );
}

type WorkflowDocumentLinkPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: string;
  selectedDocumentId: string;
  onSelectedDocumentIdChange: (value: string) => void;
  selectedRole: string;
  onSelectedRoleChange: (value: string) => void;
  roleOptions: WorkflowDocumentRoleOption[];
  documentSearch: string;
  onDocumentSearchChange: (value: string) => void;
  searchResults: WorkflowDocumentSearchResult[];
  searchLoading: boolean;
  searchReady: boolean;
  linking: boolean;
  /** Badge shown on results from the highlighted folder (e.g. "In this grant's folder"). */
  highlightBadgeLabel?: string;
  onLink: () => void;
};

export function WorkflowDocumentLinkPickerDialog({
  open,
  onOpenChange,
  description,
  selectedDocumentId,
  onSelectedDocumentIdChange,
  selectedRole,
  onSelectedRoleChange,
  roleOptions,
  documentSearch,
  onDocumentSearchChange,
  searchResults,
  searchLoading,
  searchReady,
  linking,
  highlightBadgeLabel = "In linked folder",
  onLink,
}: WorkflowDocumentLinkPickerDialogProps) {
  const selectedDocument = searchResults.find(
    (document) => String(document.id) === selectedDocumentId,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link File Center document</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workflow-link-document-search">Search documents</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="workflow-link-document-search"
                className="pl-9"
                placeholder="Search by name, keyword, or description…"
                value={documentSearch}
                onChange={(event) => onDocumentSearchChange(event.target.value)}
                autoFocus
              />
            </div>
            {!searchReady ? (
              <p className="text-xs text-muted-foreground">
                Type at least {WORKFLOW_DOCUMENT_LINK_MIN_SEARCH_LENGTH} characters to search File
                Center.
              </p>
            ) : searchLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching documents…
              </div>
            ) : searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground">No matching documents found.</p>
            ) : (
              <ScrollArea className="h-48 rounded-md border">
                <ul className="divide-y">
                  {searchResults.map((document) => {
                    const isSelected = selectedDocumentId === String(document.id);
                    return (
                      <li key={document.id}>
                        <button
                          type="button"
                          disabled={document.alreadyLinked}
                          className={cn(
                            "flex w-full items-start justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/50",
                            isSelected && "bg-primary/10 hover:bg-primary/10",
                            document.alreadyLinked &&
                              "cursor-not-allowed text-muted-foreground hover:bg-transparent",
                          )}
                          onClick={() => onSelectedDocumentIdChange(String(document.id))}
                        >
                          <span className="line-clamp-2 leading-snug break-words [overflow-wrap:anywhere]">
                            {document.name}
                          </span>
                          {document.alreadyLinked ? (
                            <Badge
                              variant="secondary"
                              className="h-5 shrink-0 px-1.5 text-[10px] font-normal"
                            >
                              Already linked
                            </Badge>
                          ) : document.inHighlightedFolder ? (
                            <Badge
                              variant="outline"
                              className="h-5 shrink-0 px-1.5 text-[10px] font-normal"
                            >
                              {highlightBadgeLabel}
                            </Badge>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
            {selectedDocument ? (
              <p className="text-xs text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{selectedDocument.name}</span>
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="workflow-link-role">Role</Label>
            <Select value={selectedRole} onValueChange={onSelectedRoleChange}>
              <SelectTrigger id="workflow-link-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selectedDocumentId || linking} onClick={onLink}>
            {linking ? "Linking…" : "Link document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type WorkflowDocumentLinkListProps<TLink extends WorkflowDocumentLinkRecord> = {
  links: TLink[];
  isLoading: boolean;
  error: unknown;
  roleLabels: Record<string, string>;
  emptyMessage: string;
  unlinking: boolean;
  onUnlink: (linkId: number) => void;
  renderLinkActions?: (link: TLink) => ReactNode;
  /** View-only: keep Open, drop unlink and link actions (ENG-1877). */
  readOnly?: boolean;
};

export function WorkflowDocumentLinkList<TLink extends WorkflowDocumentLinkRecord = WorkflowDocumentLinkRecord>({
  links,
  isLoading,
  error,
  roleLabels,
  emptyMessage,
  unlinking,
  onUnlink,
  renderLinkActions,
  readOnly = false,
}: WorkflowDocumentLinkListProps<TLink>) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading documents…
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">Could not load linked documents.</p>;
  }

  if (links.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-6 text-center">
        <FileText className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ul className="min-w-0 space-y-2">
      {links.map((link) => (
        <li
          key={link.id}
          className="flex items-start gap-3 rounded-lg border bg-muted/20 px-3 py-2.5"
        >
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm font-medium leading-snug break-words [overflow-wrap:anywhere]">
              {link.label ?? link.name}
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Badge variant="secondary" className="h-5 shrink-0 px-1.5 text-[10px] font-normal">
                {roleLabels[link.role] ?? link.role.replace(/_/g, " ")}
              </Badge>
              <span className="text-xs text-muted-foreground">Linked by {link.linked_by_name}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 self-start pt-0.5">
            {!readOnly && renderLinkActions?.(link)}
            {link.file_path ? (
              <Button variant="ghost" size="sm" asChild>
                <a href={link.file_path} target="_blank" rel="noreferrer">
                  Open
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </Button>
            ) : null}
            {!readOnly && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Unlink ${link.name}`}
                disabled={unlinking}
                onClick={() => onUnlink(link.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

type WorkflowDocumentLinksBlockedProps = {
  testId: string;
  message: string;
};

export function WorkflowDocumentLinksBlocked({ testId, message }: WorkflowDocumentLinksBlockedProps) {
  return (
    <Alert data-testid={testId}>
      <FolderOpen className="h-4 w-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}
